import {
  type Alert,
  type Coordinate,
  type Inject,
  type Patient,
  type Scenario,
  type Unit,
  describeScenarioProblems,
  elrdAlarm,
  emptyTriageFindings,
  preTriageCategory,
  serializeScenario,
  signById,
  validateInjectEffects,
} from '@orgsim/shared';
import { useEffect, useState } from 'react';
import { saveScenario } from '../api/scenarios';
import { DrawTools } from '../map/DrawTools';
import { DrawingLayer } from '../map/DrawingLayer';
import { MapView } from '../map/MapView';
import { PatientMarker } from '../map/PatientMarker';
import { SignMarker } from '../map/SignMarker';
import { SignPalette, placementOrganisation } from '../signs/SignPalette';
import { draftFor, useAppStore } from '../store/appStore';
import { ContextMenu } from './ContextMenu';
import { Icon } from './Icon';
import { MapDock } from './MapDock';
import { downloadText } from './download';
import { InjectForm } from './editor/InjectForm';
import { PatientForm } from './editor/PatientForm';
import { UnitForm } from './editor/UnitForm';

type Tab = 'situation' | 'patients' | 'units' | 'injects';
type Menu =
  | { at: { x: number; y: number }; kind: 'unit'; unit: Unit }
  | { at: { x: number; y: number }; kind: 'drawing'; id: string };
const TABS: { id: Tab; label: string }[] = [
  { id: 'situation', label: 'Lage' },
  { id: 'patients', label: 'Patienten' },
  { id: 'units', label: 'Kräfte' },
  { id: 'injects', label: 'Einspieler' },
];

export function EditorScreen() {
  const initial = useAppStore((s) => s.scenario);
  const goStart = useAppStore((s) => s.goStart);
  const removeImported = useAppStore((s) => s.removeImported);
  const initialPublished = useAppStore((s) => s.published);
  const saveEditorDraft = useAppStore((s) => s.saveEditorDraft);
  // Read once on mount: a draft left behind when an expired session switched to the login
  // screen, restored with its unsaved changes still marked as unsaved.
  const [restored] = useState(() => draftFor(useAppStore.getState(), initial));
  const [published, setPublished] = useState(restored?.published ?? initialPublished);
  const [savedPublished, setSavedPublished] = useState(
    restored?.savedPublished ?? initialPublished,
  );
  const [scenario, setScenario] = useState<Scenario>(
    () => restored?.scenario ?? structuredClone(initial as Scenario),
  );
  const [tab, setTab] = useState<Tab>('situation');
  const [selectedSign, setSelectedSign] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [placement, setPlacement] = useState<'patient' | 'location' | null>(null);
  const [drawSession, setDrawSession] = useState(0);
  const [mapFocus, setMapFocus] = useState<{ position: Coordinate } | null>(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const [menu, setMenu] = useState<Menu | null>(null);
  // Ids of injects whose "Wirkungen" textarea currently holds text that does not parse or
  // validate. InjectForm owns the draft text locally and only commits it once it is valid, so
  // this is the only place that knows an edit is in progress and would otherwise be lost.
  const [invalidInjectIds, setInvalidInjectIds] = useState<Set<string>>(new Set());
  // Snapshot of the scenario as last saved to the server (or as freshly loaded), used to
  // detect unsaved changes. A plain string compare is enough here, no deep-diff needed.
  const [savedSnapshot, setSavedSnapshot] = useState(
    () => restored?.savedSnapshot ?? serializeScenario(scenario),
  );
  const dirty = serializeScenario(scenario) !== savedSnapshot || published !== savedPublished;
  useEffect(() => {
    saveEditorDraft({ scenario, published, savedSnapshot, savedPublished });
  }, [saveEditorDraft, scenario, published, savedSnapshot, savedPublished]);
  const elrd = elrdAlarm(scenario);

  const cancelPlacement = () => {
    setPlacement(null);
    setSelectedSign(null);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPlacement(null);
        setSelectedSign(null);
        setPanelVisible(false);
        setSelectedId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const choosePlacement = (next: 'patient' | 'location' | null) => {
    setPlacement(next);
    setSelectedSign(null);
    setDrawing(false);
    setDrawSession((s) => s + 1);
    if (next) {
      setTab(next === 'patient' ? 'patients' : 'situation');
      setPanelVisible(false);
    }
  };

  const chooseSign = (sign: string | null) => {
    setSelectedSign(sign);
    setPlacement(null);
    setDrawing(false);
    setDrawSession((s) => s + 1);
    if (sign) setPanelVisible(false);
  };

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const update = (patch: Partial<Scenario>) => {
    setScenario((s) => ({ ...s, ...patch }));
    setMessage(null);
  };

  const setInjectValidity = (id: string, valid: boolean) =>
    setInvalidInjectIds((prev) => {
      const isInvalid = prev.has(id);
      if (isInvalid === !valid) return prev;
      const next = new Set(prev);
      if (valid) next.delete(id);
      else next.add(id);
      return next;
    });

  const onMapClick = (position: Coordinate) => {
    // Leaflet fires every click listener registered on the map, so DrawTools cannot
    // suppress this one on its own; the drawing flag gates it here instead.
    if (drawing) return;
    if (selectedSign) {
      const def = signById(selectedSign);
      const unit: Unit = {
        id: crypto.randomUUID(),
        type: selectedSign,
        callsign: def.label,
        status: 'onScene',
        position,
        organisation: placementOrganisation(selectedSign),
      };
      update({ units: [...scenario.units, unit] });
      setSelectedSign(null);
      setSelectedId(unit.id);
      setTab('units');
      setPanelVisible(true);
      return;
    }
    if (placement === 'patient') {
      // Derived from the highest existing number, not the array length: deleting a patient
      // and adding a new one must not reissue a number that is still in use.
      const number = scenario.patients.reduce((max, p) => Math.max(max, p.number), 0) + 1;
      const triage = emptyTriageFindings();
      const patient: Patient = {
        id: crypto.randomUUID(),
        number,
        position,
        summary: '',
        findings: [],
        triage,
        expectedCategory: preTriageCategory(triage, scenario.limitedResources ?? false),
      };
      update({ patients: [...scenario.patients, patient] });
      setSelectedId(patient.id);
      setPlacement(null);
      setPanelVisible(true);
      return;
    }
    if (placement === 'location') {
      update({ situation: { ...scenario.situation, location: position } });
      setPlacement(null);
    }
    setPanelVisible(false);
    setSelectedId(null);
  };

  // Shared by save and export: a scenario that fails scenarioSchema (empty name, a
  // non-positive patient number, ...) must not be written or downloaded either.
  const scenarioProblems = (s: Scenario) => [
    ...describeScenarioProblems(s),
    ...validateInjectEffects(s),
  ];

  const save = async () => {
    const draftProblems = scenario.injects
      .filter((i) => invalidInjectIds.has(i.id))
      .map((i) => `Einspieler "${i.title}": Wirkungen sind kein gültiges JSON.`);
    if (draftProblems.length) {
      setMessage(draftProblems.join('\n'));
      return;
    }
    const problems = scenarioProblems(scenario);
    if (problems.length) {
      setMessage(problems.join('\n'));
      return;
    }
    try {
      await saveScenario(scenario, published);
    } catch (e) {
      setMessage(`Nicht gesichert: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    // The server copy is now the current one; a stale imported copy would shadow it.
    removeImported(scenario.id);
    setSavedSnapshot(serializeScenario(scenario));
    setSavedPublished(published);
    setMessage('Gespeichert.');
  };

  const exportScenario = () => {
    const problems = scenarioProblems(scenario);
    if (problems.length) {
      setMessage(problems.join('\n'));
      return;
    }
    downloadText(`${scenario.name}.orgsim.json`, serializeScenario(scenario));
  };

  const back = () => {
    if (dirty && !window.confirm('Ungespeicherte Änderungen gehen verloren. Trotzdem zurück?')) {
      return;
    }
    goStart();
  };

  // Creates `alert` once a field is filled in, and drops it again once every field is
  // empty, so a scenario without an alarm dispatch stays exactly that in the saved file.
  const setAlert = (patch: Partial<Alert>) => {
    const base: Alert = scenario.alert ?? { time: '', address: '', message: '', units: '' };
    const next = { ...base, ...patch };
    const empty = !next.time && !next.address && !next.message && !next.units;
    update({ alert: empty ? undefined : next });
  };

  // The switch changes the category of every patient, not just new ones: re-derive all of
  // them so the scenario never carries a category the algorithm no longer produces (the
  // Task 14 consistency test would otherwise catch this later). `limited || undefined`
  // mirrors setAlert below: flipping the switch on and back off must leave the serialized
  // scenario unchanged, so the dirty check does not flag a change the instructor did not make.
  const setLimitedResources = (limited: boolean) => {
    const limitedResources = limited || undefined;
    const patients = scenario.patients.map((p) => ({
      ...p,
      expectedCategory: preTriageCategory(p.triage, limited),
    }));
    update({ limitedResources, patients });
  };

  const replacePatient = (p: Patient) =>
    update({ patients: scenario.patients.map((x) => (x.id === p.id ? p : x)) });
  const replaceUnit = (u: Unit) =>
    update({ units: scenario.units.map((x) => (x.id === u.id ? u : x)) });
  const replaceInject = (i: Inject) =>
    update({ injects: scenario.injects.map((x) => (x.id === i.id ? i : x)) });
  const removeInject = (id: string) => {
    update({ injects: scenario.injects.filter((x) => x.id !== id) });
    setInjectValidity(id, true);
  };

  const selectPatient = (p: Patient) => {
    choosePlacement(null);
    setTab('patients');
    setSelectedId(p.id);
    setPanelVisible(true);
  };

  const closeDetails = () => {
    setPanelVisible(false);
    setSelectedId(null);
  };
  const openArea = (next: Tab) => {
    setPanelVisible(!(panelVisible && tab === next && !selectedId));
    setTab(next);
    setSelectedId(null);
    choosePlacement(null);
  };

  const menuItems = () => {
    if (!menu) return [];
    if (menu.kind === 'unit') {
      const unit = menu.unit;
      return [
        {
          label: 'Umbenennen',
          onClick: () => {
            const callsign = window.prompt('Funkrufname', unit.callsign);
            if (callsign) replaceUnit({ ...unit, callsign });
          },
        },
        {
          label: 'Entfernen',
          danger: true,
          onClick: () => update({ units: scenario.units.filter((x) => x.id !== unit.id) }),
        },
      ];
    }
    return [
      {
        label: 'Entfernen',
        danger: true,
        onClick: () => update({ drawings: scenario.drawings.filter((d) => d.id !== menu.id) }),
      },
    ];
  };

  return (
    <div className="workspace simulation-workspace">
      <header className="workspace-header simulation-header">
        <div className="workspace-back">
          <button
            type="button"
            onClick={back}
            className="button quiet"
            aria-label="Zur Szenarioübersicht"
          >
            <Icon name="back" />
            Szenarien
          </button>
        </div>
        <input
          value={scenario.name}
          onChange={(e) => update({ name: e.target.value })}
          className="rounded bg-neutral-800 px-2 py-1"
          aria-label="Name des Szenarios"
        />
        <select
          aria-label="Prüfungsmodus"
          value={scenario.mode}
          onChange={(e) => update({ mode: e.target.value as Scenario['mode'] })}
          className="rounded bg-neutral-800 px-2 py-1 text-sm"
        >
          <option value="orgl">OrgL</option>
          <option value="lna">LNA</option>
          <option value="both">OrgL und LNA</option>
        </select>
        <div className="button-group">
          <label className="save-status flex items-center gap-1">
            <input
              type="checkbox"
              checked={published}
              onChange={(e) => {
                setPublished(e.target.checked);
                setMessage(null);
              }}
            />
            Für Ausbilder freigegeben
          </label>
          <span className="save-status">
            {dirty ? 'Ungespeicherte Änderungen' : 'Szenario bearbeiten'}
          </span>
          <button type="button" onClick={exportScenario} className="button">
            <Icon name="download" size={16} />
            Exportieren
          </button>
          <button type="button" onClick={save} className="button primary">
            <Icon name="check" size={16} />
            Speichern
          </button>
        </div>
      </header>
      {message ? (
        <output className={`notice ${message === 'Gespeichert.' ? '' : 'warning'}`}>
          {message}
        </output>
      ) : null}
      <div className="work-area simulation-area">
        <div className="map-workspace">
          <div className="map-toolbar" aria-label="Kartenwerkzeuge">
            <button
              type="button"
              className="button quiet"
              aria-pressed={!placement && !selectedSign && !drawing}
              onClick={() => choosePlacement(null)}
            >
              <Icon name="cursor" size={16} />
              Auswählen
            </button>
            <button
              type="button"
              className="button quiet"
              aria-pressed={placement === 'patient'}
              onClick={() => choosePlacement(placement === 'patient' ? null : 'patient')}
            >
              <Icon name="people" size={16} />
              Patient setzen
            </button>
            <button
              type="button"
              className="button quiet"
              aria-pressed={placement === 'location'}
              onClick={() => choosePlacement(placement === 'location' ? null : 'location')}
            >
              <Icon name="pin" size={16} />
              Einsatzort
            </button>
            <button
              type="button"
              className="button quiet toolbar-end"
              title="Zum Einsatzort"
              aria-label="Zum Einsatzort"
              onClick={() => setMapFocus({ position: scenario.situation.location })}
            >
              <Icon name="target" size={17} />
            </button>
          </div>
          <div className={`map-canvas ${placement || selectedSign ? 'is-placing' : ''}`}>
            <MapView
              center={scenario.situation.location}
              zoom={5}
              onClick={onMapClick}
              location={scenario.situation.location}
              focus={mapFocus}
            >
              {scenario.units
                .filter((u) => u.position)
                .map((u) => (
                  <SignMarker
                    key={u.id}
                    sign={{
                      id: u.id,
                      signType: u.type,
                      position: u.position as Coordinate,
                      label: u.callsign,
                      organisation: u.organisation,
                    }}
                    onMove={(position) => replaceUnit({ ...u, position })}
                    selected={panelVisible && selectedId === u.id}
                    onClick={() => {
                      choosePlacement(null);
                      setTab('units');
                      setSelectedId(u.id);
                      setPanelVisible(true);
                    }}
                    onContextMenu={(_, at) => setMenu({ at, kind: 'unit', unit: u })}
                  />
                ))}
              {scenario.patients.map((p) => (
                <PatientMarker
                  key={p.id}
                  patient={p}
                  draggable
                  onMove={(position) => replacePatient({ ...p, position })}
                  onClick={() => selectPatient(p)}
                  selected={panelVisible && selectedId === p.id}
                />
              ))}
              <DrawingLayer
                drawings={scenario.drawings}
                onContextMenu={(id, at) => setMenu({ at, kind: 'drawing', id })}
              />
              <DrawTools
                key={drawSession}
                onDrawing={(d) => update({ drawings: [...scenario.drawings, d] })}
                drawingActive={setDrawing}
                onActivate={() => {
                  cancelPlacement();
                  closeDetails();
                }}
              />
            </MapView>
            {placement || selectedSign ? (
              <output className="placement-banner">
                <Icon name="pin" size={16} />
                <span>
                  {placement === 'patient'
                    ? 'Auf die Karte klicken, um einen Patienten anzulegen.'
                    : placement === 'location'
                      ? 'Neuen Einsatzort auf der Karte wählen.'
                      : `${signById(selectedSign as string).label}: Position auf der Karte wählen.`}
                </span>
                <button type="button" className="button" onClick={cancelPlacement}>
                  Abbrechen <kbd>Esc</kbd>
                </button>
              </output>
            ) : null}
          </div>
        </div>
        <MapDock
          active={panelVisible ? tab : null}
          onSelect={openArea}
          items={TABS.map((t) => ({
            ...t,
            icon:
              t.id === 'situation'
                ? 'map'
                : t.id === 'patients'
                  ? 'people'
                  : t.id === 'units'
                    ? 'truck'
                    : 'bolt',
            count:
              t.id === 'patients'
                ? scenario.patients.length
                : t.id === 'units'
                  ? scenario.units.length
                  : t.id === 'injects'
                    ? scenario.injects.length
                    : undefined,
          }))}
        />
        {!panelVisible && !placement && !selectedSign && !drawing ? (
          <div className="map-interaction-hint">
            <Icon name="cursor" size={15} />
            Objekte anklicken zum Bearbeiten · Ziehen zum Verschieben
          </div>
        ) : null}
        <aside
          className="inspector context-inspector"
          hidden={!panelVisible}
          aria-label="Szenariodetails"
        >
          <div className="drawer-heading">
            <h2>
              {selectedId && tab === 'patients'
                ? 'Patient bearbeiten'
                : selectedId && tab === 'units'
                  ? 'Kraft bearbeiten'
                  : TABS.find((t) => t.id === tab)?.label}
            </h2>
            <button
              type="button"
              className="button quiet icon-button"
              aria-label="Details schließen"
              onClick={closeDetails}
            >
              <Icon name="close" />
            </button>
          </div>
          {selectedId && (tab === 'patients' || tab === 'units') ? (
            <button type="button" className="all-objects" onClick={() => setSelectedId(null)}>
              <Icon name="back" size={14} />
              {tab === 'patients' ? 'Alle Patienten' : 'Alle Kräfte'}
            </button>
          ) : null}
          <div className="inspector-content space-y-3">
            {tab === 'situation' ? (
              <>
                <label className="block">
                  Stichwort
                  <input
                    value={scenario.situation.keyword}
                    onChange={(e) =>
                      update({ situation: { ...scenario.situation, keyword: e.target.value } })
                    }
                    className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
                  />
                </label>
                <label className="block">
                  Lagetext
                  <textarea
                    value={scenario.situation.text}
                    onChange={(e) =>
                      update({ situation: { ...scenario.situation, text: e.target.value } })
                    }
                    rows={6}
                    className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
                  />
                </label>
                <p className="text-xs text-neutral-500">
                  Einsatzort: ({scenario.situation.location.x}, {scenario.situation.location.y}).
                </p>
                <fieldset className="space-y-2 rounded border border-neutral-800 p-2">
                  <legend className="px-1 text-xs uppercase tracking-wide text-neutral-400">
                    Melder
                  </legend>
                  <p className="text-xs text-neutral-500">
                    Was auf dem Meldeempfänger steht. Leer lassen, wenn das Szenario ohne
                    Alarmierung beginnt.
                  </p>
                  <label className="block">
                    Alarmzeit
                    <input
                      value={scenario.alert?.time ?? ''}
                      onChange={(e) => setAlert({ time: e.target.value })}
                      placeholder="14:32"
                      className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
                    />
                  </label>
                  <label className="block">
                    Einsatzort im Klartext
                    <input
                      value={scenario.alert?.address ?? ''}
                      onChange={(e) => setAlert({ address: e.target.value })}
                      className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
                    />
                  </label>
                  <label className="block">
                    Meldebild
                    <textarea
                      value={scenario.alert?.message ?? ''}
                      onChange={(e) => setAlert({ message: e.target.value })}
                      rows={3}
                      className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
                    />
                  </label>
                  <label className="block">
                    Alarmiert
                    <input
                      value={scenario.alert?.units ?? ''}
                      onChange={(e) => setAlert({ units: e.target.value })}
                      placeholder="RTW 1, NEF 1, OrgL"
                      className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
                    />
                  </label>
                  <div className="space-y-1 rounded border border-neutral-800 p-2">
                    <p className="text-xs uppercase tracking-wide text-neutral-400">
                      Alarmierung nach VA EL-RD
                    </p>
                    <p>
                      Empfehlung:{' '}
                      <strong>
                        {elrd.recommendation === 'orgl+lna' ? 'OrgL + LNA' : 'OrgL allein'}
                      </strong>
                    </p>
                    <p className="text-xs text-neutral-500">Begründung: {elrd.because}</p>
                    <p className="text-xs text-neutral-500">
                      {elrd.injured} Verletzte · {elrd.doctors} NAs im Einsatz
                    </p>
                    <p className="text-xs text-neutral-500">
                      Einsatzgrund „mehr als 4 Verletzte“: {elrd.reasonMet ? 'ja' : 'nein'}
                      {!elrd.reasonMet
                        ? ' (EL-RD nur bei erhöhtem Koordinierungsbedarf oder Nachforderung)'
                        : ''}
                    </p>
                    {elrd.findings.map((f, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: findings have no id, order is the identity
                      <p key={i} className={`notice ${f.level === 'warning' ? 'warning' : ''}`}>
                        {f.message}
                      </p>
                    ))}
                  </div>
                </fieldset>
                <div className="flex gap-2">
                  <label className="flex flex-1 items-start gap-2 rounded border border-neutral-800 p-2">
                    <input
                      type="checkbox"
                      checked={scenario.limitedResources ?? false}
                      onChange={(e) => setLimitedResources(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      Limitierte Ressourcen
                      <span className="block text-xs text-neutral-500">
                        Katastrophenfall. Erst dann wird nach der Überlebenschance gefragt und
                        Kategorie IV ist erreichbar.
                      </span>
                    </span>
                  </label>
                  <label className="flex flex-1 items-start gap-2 rounded border border-neutral-800 p-2">
                    <input
                      type="checkbox"
                      checked={scenario.disaster ?? false}
                      onChange={(e) => update({ disaster: e.target.checked || undefined })}
                      className="mt-0.5"
                    />
                    <span>
                      Katastrophenlage
                      <span className="block text-xs text-neutral-500">
                        Löst nach VA EL-RD immer die kombinierte Alarmierung von OrgL und LNA aus.
                      </span>
                    </span>
                  </label>
                </div>
              </>
            ) : null}
            {tab === 'patients' ? (
              <>
                {!selectedId ? (
                  <button
                    type="button"
                    className="button"
                    onClick={() => choosePlacement('patient')}
                  >
                    <Icon name="plus" size={16} />
                    Patient auf Karte hinzufügen
                  </button>
                ) : null}
                {scenario.patients.length === 0 ? (
                  <p className="panel-description">
                    Noch keine Patienten. Wähle eine Position auf der Karte und ergänze anschließend
                    die Befunde.
                  </p>
                ) : null}
                {scenario.patients
                  .filter((p) => !selectedId || selectedId === p.id)
                  .map((p) => (
                    <PatientForm
                      key={p.id}
                      patient={p}
                      limitedResources={scenario.limitedResources ?? false}
                      open={selectedId === p.id}
                      onOpen={() => {
                        setSelectedId(selectedId === p.id ? null : p.id);
                        setMapFocus({ position: p.position });
                      }}
                      onChange={replacePatient}
                      onRemove={() => {
                        update({ patients: scenario.patients.filter((x) => x.id !== p.id) });
                        closeDetails();
                      }}
                    />
                  ))}
              </>
            ) : null}
            {tab === 'units' ? (
              <>
                {!selectedId ? (
                  <>
                    <SignPalette selected={selectedSign} onSelect={chooseSign} />
                    <p className="text-xs text-neutral-500">
                      Ohne Position angelegte Kräfte gelten als alarmiert und erscheinen erst, wenn
                      der Ausbilder sie platziert.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        const u: Unit = {
                          id: crypto.randomUUID(),
                          type: 'rtw',
                          callsign: 'RTW',
                          status: 'alerted',
                        };
                        update({ units: [...scenario.units, u] });
                        setSelectedId(u.id);
                      }}
                      className="rounded bg-neutral-800 px-2 py-1"
                    >
                      Kraft für spätere Ankunft anlegen
                    </button>
                  </>
                ) : null}
                {scenario.units
                  .filter((u) => !selectedId || selectedId === u.id)
                  .map((u) => (
                    <UnitForm
                      key={u.id}
                      unit={u}
                      open={selectedId === u.id}
                      onOpen={() => {
                        setSelectedId(selectedId === u.id ? null : u.id);
                        if (u.position) setMapFocus({ position: u.position });
                      }}
                      onChange={replaceUnit}
                      onRemove={() => {
                        update({ units: scenario.units.filter((x) => x.id !== u.id) });
                        closeDetails();
                      }}
                    />
                  ))}
              </>
            ) : null}
            {tab === 'injects' ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    const i: Inject = {
                      id: crypto.randomUUID(),
                      title: 'Neuer Einspieler',
                      text: '',
                    };
                    update({ injects: [...scenario.injects, i] });
                    setSelectedId(i.id);
                  }}
                  className="rounded bg-neutral-800 px-2 py-1"
                >
                  Einspieler hinzufügen
                </button>
                {scenario.injects.map((i) => (
                  <InjectForm
                    key={i.id}
                    inject={i}
                    scenario={scenario}
                    open={selectedId === i.id}
                    onOpen={() => setSelectedId(i.id)}
                    onChange={replaceInject}
                    onRemove={() => removeInject(i.id)}
                    onValidChange={(valid) => setInjectValidity(i.id, valid)}
                  />
                ))}
              </>
            ) : null}
          </div>
        </aside>
      </div>
      <footer className="workspace-footer">
        <span>
          {drawing
            ? 'Zeichenwerkzeug aktiv'
            : placement || selectedSign
              ? 'Platzierung aktiv · Esc bricht ab'
              : 'Karte ziehen zum Navigieren · Objekte anklicken zum Bearbeiten'}
        </span>
        <span>Rechtsklick auf ein Objekt für weitere Aktionen</span>
      </footer>
      {menu ? <ContextMenu at={menu.at} items={menuItems()} onClose={() => setMenu(null)} /> : null}
    </div>
  );
}
